import { readMe } from '@directus/sdk'
import directus from '@/lib/directus'
import { User } from '@/types'

interface AuthResponse {
  success: boolean
  error?: Error
  user?: User
  token?: string
  redirectTo?: string
}

interface LoginCredentials {
  email: string
  password: string
}

interface RegisterData {
  email: string
  password: string
  firstName: string
  lastName: string
  phoneNumber?: string
}

export const authService = {
  /**
   * Login with email and password using Directus SDK
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const result = await directus.login(credentials.email, credentials.password)

      // Get current user info
      const me = await directus.request(readMe())

      // Store auth data
      localStorage.setItem('directus_auth', JSON.stringify(result))
      if (result.access_token) {
        localStorage.setItem('access_token', result.access_token)
      }
      if (result.refresh_token) {
        localStorage.setItem('refresh_token', result.refresh_token)
      }

      // Store user data
      const userData: User = {
        id: me.id,
        email: me.email,
        displayName: `${me.first_name || ''} ${me.last_name || ''}`.trim() || me.email,
        commercialId: (me.commercialId as string | undefined) ?? this.generateCommercialId(),
        referralCode: (me.referralCode as string | undefined) ?? this.generateReferralCode(),
        totalEarnings: me.totalEarnings || 0,
        referralCount: me.referralCount || 0,
        balance: me.balance || 0,
        accountType: me.accountType || 'MOBCASH',
        createdAt: me.date_created ? new Date(me.date_created) : new Date(),
        lastActivity: me.date_updated ? new Date(me.date_updated) : new Date(),
        photoURL: ((me.avatar as string | null) || null) || undefined
      }

      localStorage.setItem('userData', JSON.stringify(userData))

      return {
        success: true,
        user: userData,
        token: result.access_token || undefined,
        redirectTo: '/affiliate'
      }
    } catch (error: any) {
      console.error('Login error:', error)
      return {
        success: false,
        error: new Error(error.message || 'Email ou mot de passe incorrect')
      }
    }
  },

  /**
   * Register new user using Directus
   */
  async register(data: RegisterData): Promise<AuthResponse> {
    try {
      const commercialId = this.generateCommercialId()
      const referralCode = this.generateReferralCode()

      // Create user via Directus users endpoint
      const createUserResponse = await fetch('https://ftx-pay.futurix.xyz/users/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: data.email,
          password: data.password,
          first_name: data.firstName,
          last_name: data.lastName,
          commercialId,
          referralCode,
          totalEarnings: 0,
          referralCount: 0,
          balance: 0,
          accountType: 'MOBCASH'
        })
      })

      if (createUserResponse.ok) {
        // Auto-login after registration
        const loginResponse = await this.login({
          email: data.email,
          password: data.password
        })

        return loginResponse
      } else {
        const errorData = await createUserResponse.json()
        return {
          success: false,
          error: new Error(errorData.message || 'Erreur lors de la création du compte')
        }
      }
    } catch (error: any) {
      console.error('Register error:', error)
      return {
        success: false,
        error: new Error(error.message || 'Erreur lors de l\'inscription')
      }
    }
  },

  /**
   * Logout user using Directus SDK
   */
  async logout(): Promise<void> {
    try {
      await directus.logout()
      localStorage.removeItem('directus_auth')
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('userData')
    } catch (error) {
      console.error('Logout error:', error)
      // Always clear local data even if API call fails
      localStorage.removeItem('directus_auth')
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('userData')
    }
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token')
  },

  /**
   * Get current authenticated user
   */
  getCurrentUser(): User | null {
    try {
      const userData = localStorage.getItem('userData')
      return userData ? JSON.parse(userData) : null
    } catch (error) {
      console.error('Error parsing user data:', error)
      return null
    }
  },

  /**
   * Get auth token
   */
  getAuthToken(): string | null {
    return localStorage.getItem('access_token')
  },

  /**
   * Set auth token
   */
  setAuthToken(token: string): void {
    localStorage.setItem('access_token', token)
  },

  /**
   * Generate unique commercial ID
   */
  generateCommercialId(): string {
    const timestamp = Date.now().toString(36)
    const random = Math.random().toString(36).substring(2, 8)
    return `MB${timestamp.toUpperCase()}${random.toUpperCase()}`
  },

  /**
   * Generate unique referral code
   */
  generateReferralCode(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
    let result = ''
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    return result
  },

  /**
   * Update current user data
   */
  updateCurrentUser(userData: Partial<User>): void {
    const currentUser = this.getCurrentUser()
    if (currentUser) {
      const updatedUser = { ...currentUser, ...userData }
      localStorage.setItem('userData', JSON.stringify(updatedUser))
    }
  },

  /**
   * Refresh user data from Directus API
   */
  async refreshUserData(): Promise<AuthResponse> {
    try {
      const me = await directus.request(readMe())

      const userData: User = {
        id: me.id,
        email: me.email,
        displayName: `${me.first_name || ''} ${me.last_name || ''}`.trim() || me.email,
        commercialId: me.commercialId,
        referralCode: me.referralCode,
        totalEarnings: me.totalEarnings || 0,
        referralCount: me.referralCount || 0,
        balance: me.balance || 0,
        accountType: me.accountType || 'MOBCASH',
        createdAt: me.date_created ? new Date(me.date_created) : new Date(),
        lastActivity: me.date_updated ? new Date(me.date_updated) : new Date(),
        photoURL: ((me.avatar as string | null) || null) || undefined
      }

      this.updateCurrentUser(userData)

      return {
        success: true,
        user: userData
      }
    } catch (error: any) {
      return {
        success: false,
        error: new Error(error.message || 'Erreur lors du rafraîchissement')
      }
    }
  }
}

export default authService